import * as Antd from 'antd';
import { create } from 'zustand';

import { Theme } from '@/types';

type Store = {
    loading: boolean;
    theme: Theme;
    fullWidth: boolean;
    notification: Antd.NotificationArgsProps | null;
    supportTicketModalOpen: boolean;
    projectCrmFloatUrl: string | null;
    pageTitle: string | null;
    setLoading: (loading: boolean) => void;
    setTheme: (theme: Theme) => void;
    setFullWidth: (fullWidth: boolean) => void;
    setNotification: (notification: Antd.NotificationArgsProps | null) => void;
    setSupportTicketModalOpen: (open: boolean) => void;
    setProjectCrmFloatUrl: (url: string | null) => void;
    setPageTitle: (title: string | null) => void;
};

const darkColorSchemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

/**
 * Get the current theme from localStorage or return 'light' if not found
 * @returns The current theme from localStorage or 'light' if not found
 */
const getCurrentTheme = (): Theme => {
    const currentTheme = localStorage.getItem('app-theme') as Theme;

    if (currentTheme === 'light' || currentTheme === 'dark') {
        return currentTheme;
    }

    return darkColorSchemeMediaQuery.matches ? 'dark' : 'light';
};

export const useUIStore = create<Store>((set) => ({
    loading: true,
    theme: getCurrentTheme(),
    fullWidth: localStorage.getItem('app-full-width') === 'true',
    notification: null,
    supportTicketModalOpen: false,
    projectCrmFloatUrl: null,
    pageTitle: null,

    setLoading: (loading: boolean) => set({ loading }),
    setTheme: (theme: Theme) => {
        localStorage.setItem('app-theme', theme);
        set({ theme });
    },
    setFullWidth: (fullWidth: boolean) => {
        localStorage.setItem('app-full-width', String(fullWidth));
        set({ fullWidth });
    },
    setNotification: (notification: Antd.NotificationArgsProps | null) => set({ notification }),
    setSupportTicketModalOpen: (open: boolean) => set({ supportTicketModalOpen: open }),
    setProjectCrmFloatUrl: (url: string | null) => set({ projectCrmFloatUrl: url }),
    setPageTitle: (title: string | null) => set({ pageTitle: title }),
}));

darkColorSchemeMediaQuery.addEventListener('change', (event) => {
    useUIStore.setState({ theme: event.matches ? 'dark' : 'light' });
});
